import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Flex, message } from "antd";
import React, { useMemo, useState } from "react";
import NoteCopyButton from "../../components/CopyButton/CopyButton.component";
import TransferToEHRButton from "../../components/TransferButton/TransferButton.component";
import { DAP_FIELDS, NoteType, SOAP_FIELDS, isCustomisedTemplate } from "../../domain/notes.domain";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { getCustomNote } from "../../service/notes.service";
import {
  setAITemplateModalState,
  setCreateModalState,
  setCustomiseSectionState,
  setEditingState,
  setSelectedNoteForEditing,
  setShowRestrictionModal,
  setStoredTemplateData,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { TNotes } from "../../types/index.type";
import { copyNote } from "../../utils/datamanipulation.utils";
import EditButton from "./EditButton.component";
import NoteSectionTitle from "./NoteSectionTitle.component";
import { CUSTOMISE_ICON } from "./NotesHeader.component";
import styles from "./notes.module.scss";

interface NoteTitleHeaderProps {
  title?: string;
  value?: string;
  hasCustomize?: boolean;
  children?: React.ReactNode;
}

const NoteTitleHeader: React.FC<NoteTitleHeaderProps> = ({ title, value, hasCustomize = true, children }) => {
  const componentName = "notes";
  const dispatch = useAppDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isMobile = useIsMobileView();
  const { notesDetails } = useAppSelector((state) => state.notes);
  const { isMobilePath } = useAppSelector((state) => state.appState);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const isCustomTemplateNote = useMemo(
    () => isCustomisedTemplate(notesDetails?.note_type_info?.pk as number, notesDetails?.note_type as number),
    [notesDetails],
  );
  const setEditingMode = (value: boolean, notes: TNotes) => {
    dispatch(setEditingState(value));
    dispatch(setCreateModalState(true));
    dispatch(setSelectedNoteForEditing(notes));
    dispatch(
      setRecordingDetail({
        category_type: notes.category_type,
        language_type: notes.language_type,
        note_type: notes.note_type,
        title: notes.title,
        gender_type: notes.gender_type,
        modality_type: notes.modality_type.toString(),
        location_type: notes.location_type,
        duration_type: notes.duration_type,
        duration_addon_type: notes.duration_addon_type,
        client_id: notes.client,
      }),
    );
  };
  const copyAllNoteContent = () => {
    if (!notesDetails) return;
    copyNote(notesDetails, "", "");
    message.success("Note copied to clipboard");
  };

  const openAITemplateModal = () => {
    if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
    }
  };

  const shouldShowCustomizeButton =
    notesDetails?.note_type === NoteType.SOAP || notesDetails?.note_type === NoteType.DAP;

  const items = isCustomTemplateNote
    ? [
        {
          key: "1",
          label:
            userInfo.practice_area === PRACTICE_AREA.MEDICINE
              ? "Add one-time instructions to fix this note"
              : "Add my instructions",
          onClick: () => {
            dispatch(setCustomiseSectionState(true));
          },
        },
        {
          key: "2",
          label:
            userInfo.practice_area === PRACTICE_AREA.MEDICINE
              ? "Add permanent Prompt Instructions to this template"
              : "Edit Template",
          onClick: async () => {
            if (isMobilePath) {
              dispatch(setShowRestrictionModal(true));
              return;
            }
            const loading = message.loading("loading template...");
            if (!notesDetails || !notesDetails.note_type_info || !notesDetails.note_type_info.pk) {
              loading();
              return;
            }
            const template = await getCustomNote(notesDetails.note_type_info.pk as number);
            loading();
            dispatch(
              setStoredTemplateData({
                pk: notesDetails.note_type_info.pk,
                ...template.data,
              }),
            );
            openAITemplateModal();
          },
        },
      ]
    : [
        {
          key: "1",
          label:
            userInfo.practice_area === PRACTICE_AREA.MEDICINE
              ? "Add one-time instructions to fix this note"
              : "Add my instructions",
          onClick: () => {
            dispatch(setCustomiseSectionState(true));
          },
        },
        {
          key: "2",
          label: "Create a template out of this note",
          onClick: async () => {
            if (isMobilePath) {
              dispatch(setShowRestrictionModal(true));
              return;
            }
            let document: string | undefined = "";
            if (shouldShowCustomizeButton) {
              if (notesDetails?.note_type === NoteType.DAP) {
                document = DAP_FIELDS.map(
                  (field) => `${field.title}:\n${notesDetails[field.object as keyof TNotes] as string}`,
                ).join("\n\n");
              } else {
                document = SOAP_FIELDS.map(
                  (field) => `${field.title}:\n${notesDetails[field.object as keyof TNotes] as string}`,
                ).join("\n\n");
              }
            } else {
              document = value;
            }
            dispatch(
              setStoredTemplateData({
                document,
              }),
            );
            openAITemplateModal();
          },
        },
      ];

  const CustomiseButton = ({ className = "" }: { className?: string }) => {
    return (
      <Button
        type="default"
        icon={CUSTOMISE_ICON}
        className={`mobile-action-button primary ${className}`}
        style={{ boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)" }}
        onClick={() => {
          dispatch(setCustomiseSectionState(true));
        }}
      >
        Improve my note quality
      </Button>
    );
  };

  const ImproveQualityDropdown = ({ className = "" }: { className?: string }) => {
    const SPARKLE_ICON_PATH =
      "M2.83333 1V3.66667M1.5 2.33333H4.16667M3.5 10.3333V13M2.16667 11.6667H4.83333M8.16667 1L9.69048 5.57143L13.5 7L9.69048 8.42857L8.16667 13L6.64286 8.42857L2.83333 7L6.64286 5.57143L8.16667 1Z";

    return (
      <Dropdown menu={{ items }} placement="bottomLeft" onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
        <Button
          className={`${styles[`${componentName}__main-section-cust`]} ${className}`}
          type="primary"
          style={{ boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)" }}
          icon={
            <svg
              width="15"
              height="14"
              viewBox="0 0 15 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ transition: "transform 0.3s ease" }}
            >
              <path
                d={SPARKLE_ICON_PATH}
                stroke="#6366f1"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        >
          Improve my note quality{" "}
          {isDropdownOpen ? (
            <UpOutlined style={{ marginTop: 5, fontSize: 12, transition: "transform 0.3s ease" }} />
          ) : (
            <DownOutlined style={{ marginTop: 5, fontSize: 12, transition: "transform 0.3s ease" }} />
          )}
        </Button>
      </Dropdown>
    );
  };

  return (
    <Card className={`${styles[`${componentName}__main-section-header-card`]}`}>
      <Flex gap={10} vertical className={`${styles[`${componentName}__main-section-header-section`]}`}>
        <Flex vertical gap={10} className={`${styles[`${componentName}__main-section-header-section-title`]}`}>
          {title && <NoteSectionTitle title={title} />}
          <Flex
            gap={10}
            className={`${styles[`${componentName}__responsive-button-container`]}`}
            style={{ width: "100%" }}
          >
            <Flex gap={10} className={`${styles[`${componentName}__other-buttons-row`]}`}>
              <EditButton
                onClick={() => {
                  if (notesDetails) setEditingMode(true, notesDetails);
                }}
                className={`${styles[`${componentName}__edit-copy-button`]} ${styles[`${componentName}__responsive-button`]}`}
              />
              {!isMobile && (hasCustomize || shouldShowCustomizeButton) && (
                <ImproveQualityDropdown className={`${styles[`${componentName}__responsive-button`]}`} />
              )}
              {isMobile && (
                <NoteCopyButton
                  title={"Copy Note"}
                  onClick={copyAllNoteContent}
                  className={`${styles[`${componentName}__edit-copy-button`]} ${styles[`${componentName}__responsive-button`]}`}
                />
              )}
            </Flex>
            <Flex gap={10} className={`${styles[`${componentName}__other-buttons-row`]}`}>
              <TransferToEHRButton className={`${styles[`${componentName}__responsive-button`]}`} />
              {isMobile && shouldShowCustomizeButton && (
                <CustomiseButton className={`${styles[`${componentName}__responsive-button`]}`} />
              )}
              {isMobile && hasCustomize && (
                <ImproveQualityDropdown className={`${styles[`${componentName}__responsive-button`]}`} />
              )}
              {!isMobile && (
                <NoteCopyButton
                  title={"Copy Note"}
                  onClick={copyAllNoteContent}
                  className={`${styles[`${componentName}__edit-copy-button`]} ${styles[`${componentName}__responsive-button`]}`}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex className={`${styles[`${componentName}__main-section-header-section-content`]}`}>{children}</Flex>
      </Flex>
    </Card>
  );
};

export default NoteTitleHeader;
